/**
 * @fileoverview Input sanitising for mobile number fields.
 * @description Keeps a mobile number input to digits only as the user types or pastes, so letters,
 * spaces, "+" and dashes never reach the field's value — the schema in contact-fields.schema.ts
 * still validates the final value, but the user no longer has to discover a stray character only
 * on submit. Used through MOBILE_NUMBER_INPUT_PROPS's onInput handler, so every mobile field gets it.
 * Deliberately imports nothing from contact-fields.constants.ts (which imports this file) — the
 * length comes from the input's own maxLength, set by those same props — to avoid an import cycle.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { ClipboardEvent, FormEvent } from 'react'

/**
 * @description Reduces a typed or pasted value to digits only, at most `maxLength` of them. When
 * more digits than that remain (a pasted "+91 98765 43210" or "098765 43210"), the last ones are
 * kept, since a country code or trunk "0" always comes first.
 * @param {string} value - The raw input value.
 * @param {number} maxLength - The most digits to keep (10 for a mobile number). Zero or negative —
 * what a browser reports for an input with no maxLength set — means no limit.
 * @returns {string} The digits-only value, at most `maxLength` characters long.
 */
export function sanitizeMobileNumber(value: string, maxLength: number): string {
  const digits = value.replace(/\D/g, '')
  return maxLength > 0 && digits.length > maxLength ? digits.slice(-maxLength) : digits
}

/**
 * @description Native `input` handler that rewrites the field's value to its sanitised form before
 * React's onChange (and so React Hook Form's register) reads it — React dispatches onInput ahead of
 * onChange for the same input event.
 * @param {FormEvent<HTMLInputElement>} event - The input event from a mobile number field.
 */
export function keepMobileNumberDigits(event: FormEvent<HTMLInputElement>): void {
  const input = event.currentTarget
  const sanitised = sanitizeMobileNumber(input.value, input.maxLength)
  if (sanitised !== input.value) {
    input.value = sanitised
  }
}

/**
 * @description Paste handler for a mobile number field. A browser truncates pasted text to the
 * input's maxLength *before* any input event fires, so "+91 98765 43210" would arrive as
 * "+91 98765 " and lose its last digits. This cleans the clipboard text first (keeping its last 10
 * digits), splices it in place of the current selection, and writes the result through the native
 * value setter plus a bubbling `input` event, so React and React Hook Form see it like typed input.
 * @param {ClipboardEvent<HTMLInputElement>} event - The paste event from a mobile number field.
 */
export function pasteMobileNumberDigits(event: ClipboardEvent<HTMLInputElement>): void {
  const input = event.currentTarget
  const pastedDigits = sanitizeMobileNumber(event.clipboardData.getData('text'), input.maxLength)
  event.preventDefault()

  const start = input.selectionStart ?? input.value.length
  const end = input.selectionEnd ?? input.value.length
  const combined = `${input.value.slice(0, start)}${pastedDigits}${input.value.slice(end)}`
  const next = input.maxLength > 0 ? combined.slice(0, input.maxLength) : combined

  // Assigning input.value directly would be swallowed by React's own value tracking; the
  // prototype's setter plus a real input event is what React treats as a user edit.
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(input, next)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}
